import type { ImportantDateWithOccurrence, Person } from '../../api/types';

export type CanonicalDateItem = {
  id: string;
  personId: string;
  emoji: string;
  name: string;
  relation: string;
  label: string;
  date: string;
  weekday: string;
  iso: string;
  month: string;
  day: string;
  badge: string;
  daysLeft: number;
  today: boolean;
  lunar: boolean;
};

export type CanonicalSubjectItem = {
  id: string;
  emoji: string;
  name: string;
  relation: string;
  type: '사람' | '반려동물';
  count: number;
  next: string;
  badge: string;
};

export type CanonicalSoloEventItem = {
  id: string;
  personId: string;
  emoji: string;
  name: string;
  date: string;
  badge: string;
};

const emojiTagPrefix = 'daykeeper:emoji:';

export const emojiTag = (emoji: string) => `${emojiTagPrefix}${emoji}`;

export const personEmoji = (person: Person | null) => {
  const stored = person?.tags.find((tag) => tag.startsWith(emojiTagPrefix))?.slice(emojiTagPrefix.length);
  if (stored) return stored;
  return person?.targetType === 'pet' ? '🐶' : '👤';
};

const typeLabel: Record<ImportantDateWithOccurrence['type'], string> = {
  birthday: '생일',
  anniversary: '기념일',
  memorial: '기일',
  petBirthday: '생일',
  adoption: '입양일',
  vaccination: '예방접종',
  appointment: '예약',
  dday: 'D-Day',
  custom: '일정',
};

const badgeLabel = (label: string) => label.toUpperCase().replace('D-DAY', 'D-DAY');

export const toCanonicalDateItem = (entry: ImportantDateWithOccurrence): CanonicalDateItem => {
  const occurrenceDate = new Date(`${entry.occurrence.nextOccurrenceDate}T12:00:00`);
  return {
    id: entry.id,
    personId: entry.personId,
    emoji: personEmoji(entry.person),
    name: entry.person?.name ?? '나의 일정',
    relation: entry.person?.relation ?? (entry.person?.targetType === 'pet' ? '반려동물' : '일정'),
    label: entry.title || typeLabel[entry.type],
    date: new Intl.DateTimeFormat('ko-KR', { month: 'long', day: 'numeric' }).format(occurrenceDate),
    weekday: new Intl.DateTimeFormat('ko-KR', { weekday: 'long' }).format(occurrenceDate),
    iso: entry.occurrence.nextOccurrenceDate,
    month: new Intl.DateTimeFormat('en-US', { month: 'short' }).format(occurrenceDate).toUpperCase(),
    day: String(occurrenceDate.getDate()).padStart(2, '0'),
    badge: badgeLabel(entry.occurrence.dDayLabel),
    daysLeft: entry.occurrence.daysLeft,
    today: entry.occurrence.isToday,
    lunar: entry.calendarType === 'lunar',
  };
};

export const toCanonicalSubjectItem = (
  person: Person,
  dates: ImportantDateWithOccurrence[],
): CanonicalSubjectItem => {
  const nearest = dates
    .filter((entry) => entry.occurrence.daysLeft >= 0)
    .sort((a, b) => a.occurrence.daysLeft - b.occurrence.daysLeft)[0];
  return {
    id: person.id,
    emoji: personEmoji(person),
    name: person.name,
    relation: person.relation ?? (person.targetType === 'pet' ? '반려동물' : '사람'),
    type: person.targetType === 'pet' ? '반려동물' : '사람',
    count: dates.length,
    next: nearest?.title ?? '등록된 날짜 없음',
    badge: nearest ? badgeLabel(nearest.occurrence.dDayLabel) : '—',
  };
};

export const toCanonicalSoloEventItem = (entry: ImportantDateWithOccurrence): CanonicalSoloEventItem => {
  const item = toCanonicalDateItem(entry);
  return {
    id: entry.id,
    personId: entry.personId,
    emoji: item.emoji,
    name: `${item.name} · ${entry.title}`,
    date: `${item.iso.replaceAll('-', '. ')}.`,
    badge: item.badge,
  };
};

export const todayKicker = (today = new Date()) => new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
}).format(today).toUpperCase().replace(',', ' ·');
